import React, { useMemo, useState, useEffect } from "react";
import { LoadingScreen } from "chrome/LoadingScreen";
import Axios, { AxiosInstance } from "axios";
import { useAuthentication } from "./AuthenticationProvider";
import { useAppConfig } from "../util/AppConfig";
import { BrowserConsoleDebugLogger } from "../util/BrowserConsoleDebugLogger";

interface AxiosHandlers {
  secureAxios: AxiosInstance;
  insecureAxios: AxiosInstance;
  fileDownloadAxios: AxiosInstance;
}

const AxiosContext = React.createContext<AxiosHandlers>({
  secureAxios: Axios.create(),
  insecureAxios: Axios.create(),
  fileDownloadAxios: Axios.create(),
});

export const useAxios = () => React.useContext(AxiosContext);

export const AxiosProvider: React.FunctionComponent = (props) => {
  const [token, setToken] = useState<string | null>(null);
  const authentication = useAuthentication();
  let { showBrowserConsoleDebugLogs } = useAppConfig();
  const browserConsoleDebugLogger = new BrowserConsoleDebugLogger(
    showBrowserConsoleDebugLogs ?? false
  );

  useEffect(() => {
    authentication.getAccessToken().then((t) => setToken(t));
  }, [authentication]);

  const secureAxios = useMemo(() => {
    const instance = Axios.create({
      headers: { Authorization: `Bearer ${token}` },
    });

    instance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        browserConsoleDebugLogger.log(
          "SecureAxios: Attempting to refresh MSAL token..."
        );
        const newToken = await authentication.getAccessToken();
        setToken(newToken);
        instance.defaults.headers = { Authorization: `Bearer ${newToken}` };
        return Promise.reject(error);
      }
    );

    return instance;
  }, [token, authentication, browserConsoleDebugLogger]);

  const insecureAxios = useMemo(() => Axios.create({}), []);

  const fileDownloadAxios = useMemo(() => {
    const instance = Axios.create({
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });

    instance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        browserConsoleDebugLogger.log(
          "FileDownloadAxios: Attempting to refresh MSAL token..."
        );
        const newToken = await authentication.getAccessToken();
        setToken(newToken);
        instance.defaults.headers = {
          Authorization: `Bearer ${newToken}`,
        };
        if (error.response?.data instanceof Blob) {
          const errorText = await error.response.data.text();
          error.response.data = errorText;
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [token, authentication, browserConsoleDebugLogger]);

  const contextValue = useMemo(
    () => ({
      secureAxios,
      insecureAxios,
      fileDownloadAxios,
    }),
    [secureAxios, insecureAxios, fileDownloadAxios]
  );

  const authenticated = authentication.authenticated;

  return authenticated ? (
    <AxiosContext.Provider value={contextValue}>
      {props.children}
    </AxiosContext.Provider>
  ) : (
    <LoadingScreen mode="auth" />
  );
};
